// @flow
import Q from 'q';
import config from 'config';
import window from 'window';
import modals from 'modals';
import server, { lkRoute } from 'server2';
import ko from 'rgs-knockout';
import EventEmitter from 'events';

import {profile} from 'myrgs-auth-helpers-user';

import cleanupCookies from './helpers/cleanup-cookies';
import cleanupLocalStorage from './helpers/cleanup-localStorage';
import myDmsCleanupSessionStorage from './helpers/cleanup-sessionStorage';

import { ready as observableServerTimeReady } from 'observable-server-time';

//types
import type {LogContextInterface} from 'log-context';
import type { DocumentDictionaryItem } from 'calc-common';
//components
import 'ko-component-myrgs-auth2-form';

export const UserDataModelMixin = (superclass: any) => class UserDataModel extends superclass {
	constructor (...args) {
		super(...args);

		this.UserLogin = ko.pureComputed(() => profile.Login())
			.extend({toJS: true});
	}
};

async function getDocumentTypes (): Array<DocumentDictionaryItem> {
	const { responseJSON: { Data: documentTypes } } = await server.any.rest.get([
		lkRoute.prefix,
		lkRoute.data,
		lkRoute.physical,
		lkRoute.documentTypes,
	]);
	return documentTypes;
}


const getPersonalDataProcessingInfo = async () => {
	return await server.any.cmsapi.get([
		'cmsContent',
		'pdn',
	], {
		query: {
			pdnType: 'myrgs_personal_data_processing_info',
		},
	});
};

const getDmsPdnContent = async () => {
	return await server.any.rest.get([
		'lk',
		'profiles',
		'my',
		'dms',
		'policies',
		'pdAgreement',
	]);
};

type authModalOptions = {
	justHideAuthModalOnCancel?: boolean,
}

async function showAuthModal (logContext, options: authModalOptions = {}) {
	const context = await Q.all([
		config.cms.pageUrls.get(),
		config.cms.siteID.get(),
		getPersonalDataProcessingInfo(),
		getDmsPdnContent(),
	])
		// eslint-disable-next-line max-len
		.spread((pageUrls, siteID, { responseJSON: { PdnText } }, { responseJSON: { StatusText } }) => {
			return { cms: { pageUrls, siteID, pdn: PdnText, dmsPdn: StatusText } };
		});
	const identityDocumentTypes = await getDocumentTypes();
	await observableServerTimeReady();

	if (window.ThreadsWidget) {
		window.ThreadsWidget.hideChat();
	}

	return Q.promise(resolve => {
		const authModal = modals.create({
			css: {'modal-xlg': true},
			templateMode: 'content',
			template: {
				html: require('./templates/modals/auth-modal.html')(),
				data: {
					logContext,
					event: {
						authSuccess: () => {
							resolve();
							modals.hide(authModal);
						},
					},
					identityDocumentTypes,
					justHideAuthModalOnCancel: options.justHideAuthModalOnCancel,
					context,
				},
			},
			userCanClose: false,
		});
		const existingEl = window.document.getElementById('dms-re-auth-modal');
		if (!existingEl) {
			modals.show(authModal);
		}
	});
}

export async function logout () {
	return Q.all([
		server.my.cmsapi.post(['lk', 'logout']), //server2 instead just server
		config.cms.siteID.get(),
	]).spread((logoutState, siteID) => {
		cleanupCookies();
		cleanupLocalStorage();
		myDmsCleanupSessionStorage();
		window.location.href = siteID === 'ki' ? '/my/' : '/';
	}).done();
}

type ensureAuthOptions = {
	logContext?: LogContextInterface;
	token?: string;
	authModalOptions?: authModalOptions;
};

export async function ensureAuth ({
	logContext, token, authModalOptions,
}: ensureAuthOptions = {}) {
	if (token) {
		try {
			await server.my.cmsapi
				.post(['lk', 'auth', 'token'], {
					body: {
						Token: decodeURIComponent(token),
					},
				});
			await profile.update();
		} catch (error) {
			return undefined;
		}
	} else {
		await profile.update();

		if (profile.IsAuthenticated()) {
			return undefined;
		}

		return await showAuthModal(logContext, authModalOptions);
	}
}

export async function retryWithAuth<T> (
	op: () => Promise<T> | T,
	authModalOptions?: authModalOptions = {},
): Promise<T> {
	try {
		return await op();
	} catch (e) {
		const isAuthError = e.message === 'Forbidden' && e.name === 'HttpError';

		// Для распознавания ошибок axios, например из sys-ng2.
		// Такие ошибки могут попасть сюда через myrgs-auth-event-handlers.js
		const isAxiosAuthError = e.isAxiosError && e.response && e.response.status === 403;

		if (!isAuthError && !isAxiosAuthError) {
			throw e;
		}
		return await ensureAuth({ authModalOptions }).then(op);
	}
}

export async function getAuxiliaryOrderData (token: string) {
	try {
		await server.my.cmsapi
			.post(['lk', 'auth', 'token'], {
				body: {
					Token: decodeURIComponent(token),
				},
			});
		await profile.update();

		const res = await server.my.rest
			.get(['lk', 'profiles', 'my', 'auxiliaryOrderData'], {
				query: {
					token: decodeURIComponent(token),
				},
			});
		return (res && res.responseJSON.Data);
	} catch (error) {
		throw error;
	}
}

export function startAuthEventHandlers () {
	let eventEmitter;

	if (window['rgsAppLkEventEmitter']) {
		eventEmitter = window['rgsAppLkEventEmitter'];
	} else {
		eventEmitter = new EventEmitter();
		window['rgsAppLkEventEmitter'] = eventEmitter;
	}

	eventEmitter.on('call-with-auth', args => {
		retryWithAuth(args.callback, {
			justHideAuthModalOnCancel: args.justHideAuthModalOnCancel,
		});
	});
}

export {profile};

/**
 * @deprecated use `profile` instead
 */
export const user = profile;
